import {ReactElement, useEffect, useState} from "react";
import {useAppSelector} from "lib/redux/hooks";
import {authSelectors} from "features/auth/authSlice";
import {GetScanJobsResponse, JobStatus, ScanJobHistoryItem, UUID4, wineScannerApi} from "lib/api/wineScannerApi";
import {Spinner} from "components/UIComponents/Spinner";

export interface WineScannerListProps {
    onSelectJob: (jobId: UUID4) => void;
}

export function WineScannerList(props: WineScannerListProps): ReactElement {
    const customerId = useAppSelector(authSelectors.customer);
    const [data, setData] = useState<GetScanJobsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (customerId == null) return;
        setLoading(true);
        setError(null);
        wineScannerApi.getScanJobs({customer_id: customerId})
            .then(response => {
                setData(response);
                setLoading(false);
            })
            .catch(() => {
                setError('Fehler beim Laden der Scanaufträge.');
                setLoading(false);
            });
    }, [customerId]);

    if (loading) {
        return (
            <div className="flex-auto flex items-center justify-center">
                <Spinner className="w-10 h-10"/>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex-auto flex items-center justify-center">
                <span className="text-primary font-barlow">{error}</span>
            </div>
        );
    }

    const history = data?.history ?? [];

    return (
        <div className="flex-auto overflow-y-auto px-11 py-4">
            <div className="flex flex-col gap-3">
                {history.map(item => (
                    <ScanJobItem
                        key={item.job_id}
                        item={item}
                        onClick={() => props.onSelectJob(item.job_id)}
                    />
                ))}
                {history.length === 0 && (
                    <div className="text-light-text font-barlow text-center py-8">
                        Keine Scanaufträge vorhanden.
                    </div>
                )}
            </div>
        </div>
    );
}

interface ScanJobItemProps {
    item: ScanJobHistoryItem;
    onClick: () => void;
}

function ScanJobItem({item, onClick}: ScanJobItemProps): ReactElement {
    const isComplete = item.state.job_status === JobStatus.PDF_GENERATED;
    const date = new Date(item.created_at).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
    const totalWines = item.total_wines_count ?? 0;
    const recommendedWines = item.recommended_wines_count ?? 0;

    return (
        <div
            className="flex flex-col gap-2 rounded-lg border border-gray-2 p-3 cursor-pointer hover:bg-warm-gray font-barlow"
            onClick={onClick}
        >
            <div className="flex items-center justify-between">
                <span className="text-sm text-light-text">{date}</span>
                {!isComplete && (
                    <span className="rounded-full bg-primary px-3 py-0.5 text-xs text-white">
                        In Bearbeitung
                    </span>
                )}
            </div>
            <div className="font-semibold text-text">{item.customer_name}</div>
            <div className="text-sm text-light-text">
                {totalWines} Weine im Sortiment / {recommendedWines} Weine empfohlen
            </div>
        </div>
    );
}
